import { ModelHelper } from '@adp-wfn/mdf-core';
import { WfnForm } from '@adp-wfn/mdf-wc-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { renderField } from '../MetaFields';
import { MetaFieldSetProps } from './MetaFieldSetProps';

/**
 * Policy field set component that renders multiple form fields with validation
 * @param props - Component props
 * @param props.fields - Array of field configurations to render
 * @param props.value - Current form data values
 * @param props.onChange - Callback function for form data changes
 * @returns JSX element for the complete field set form
 */
export const MetaFieldSet = ({ fields, value = {}, onChange, className }: MetaFieldSetProps) => {
  if (!fields) {
    return null;
  }

  const [formData, setFormData] = useState<Record<string, any>>(value || {});

  useEffect(() => {
    setFormData(value || {});
  }, [value]);

  const handleFieldChange = useCallback((fieldId: string, newValue: any, fieldType: string) => {
    setFormData((prev) => {
      const newFormData = { ...prev, [fieldId]: newValue };
      onChange?.(newFormData, { fieldId, value: newValue, fieldType });
      return newFormData;
    });
  }, [onChange]);

  const shouldRenderField = useCallback((field, currentFormData) => {
    if (field.render === false) {
      return false;
    }

    if (field.render === true || field.render === undefined) {
      return true;
    }

    return ModelHelper.resolve(field.render, { formData: currentFormData }, 'MetaFieldSet');
  }, []);

  const visibleFields = useMemo(() =>
    (fields || []).filter((field) => shouldRenderField(field, formData)),
  [fields, formData, shouldRenderField]
  );

  const renderFieldComponent = useCallback((field: any) => {
    const fieldWithValue = {
      ...field,
      data: {
        ...field.data,
        value: formData[field.id] ?? field.data.value
      }
    };

    return (
      <div key={field.id} className="my-2" data-field-id={field.id}>
        {renderField(fieldWithValue, (_updatedField, event) => {
          handleFieldChange(field.id, event.value, field.type);
        })}
      </div>
    );
  }, [handleFieldChange, formData]);

  return (
    <WfnForm>
      <div className={className}>
        {visibleFields.map(renderFieldComponent)}
      </div>
    </WfnForm>
  );
};
